/**
 * GDSBuilder.js
 * The core web component that orchestrates the GDS builder.
 */
import { GDSApiService } from './GDSApiService.js';
import { GDSStateManager } from './GDSStateManager.js';
import { GDSUIFeedback } from './GDSUIFeedback.js';
import { GDSEventHandler } from './GDSEventHandler.js';
import { GDSStepOrchestrator  } from './GDSStepOrchestrator.js';
import { GDSProgressManager } from './GDSProgressManager.js';
import { GDSCardManager } from './GDSCardManager.js';
import { GDSModalManager } from './GDSModalManager.js';
import { GDSCartAndShareManager } from './GDSCartAndShareManager.js';
import { GDSUrlManager } from './GDSUrlManager.js';
import { GDSCardTutorial } from './GDSCardTutorial.js';

class GDSBuilder extends HTMLElement {
    constructor() {
        super();

        // Initialize managers in the correct order
        this.stateManager = new GDSStateManager();
        this.apiService = new GDSApiService();
        this.uiFeedback = new GDSUIFeedback(this);
        this.progressManager = new GDSProgressManager(this);

        // Initialize managers that don't have dependencies first
        this.cartAndShareManager = new GDSCartAndShareManager(this);

        // Create modalManager before cardManager since cardManager depends on it
        this.modalManager = new GDSModalManager(this);

        // Now create cardManager with all its dependencies ready
        this.cardManager = new GDSCardManager(this);

        // Create stepOrchestrator after all managers it might need
        this.stepOrchestrator = new GDSStepOrchestrator(this);

        // Event handler last as it needs access to all other managers
        this.eventHandler = new GDSEventHandler(this);

        // Initialize URL manager if it's used
        this.urlManager = new GDSUrlManager(this);
        this.cardTutorial = new GDSCardTutorial(this);
    }

    /**
     * Web component lifecycle method. Fires when the element is added to the DOM.
     */
    connectedCallback() {
        this.initRefs();

        // Verify all critical managers are initialized
        if (!this.modalManager) {
            console.error('CRITICAL: modalManager not initialized');
            this.uiFeedback.showError('Failed to initialize application components');
            return;
        }

        // Additional verification
        if (!this.verifyGDSInitialization()) {
            console.error('CRITICAL: Not all required managers are initialized');
            this.uiFeedback.showError('Failed to initialize application components');
            return;
        }

        this.uiFeedback.showLoading();
        this.initialize();
        this.eventHandler.setupAllListeners();
    }

    /**
     * Web component lifecycle method. Fires when the element is removed from the DOM.
     */
    disconnectedCallback() {
        this.eventHandler.removeAllListeners();
        this.modalManager.removeGlobalListeners();
    }

    /**
     * Finds and stores references to essential STATIC DOM elements within the component.
     * References to dynamic elements (like the next button) will be re-established by the renderer.
     */
    initRefs() {
        this.contentContainer = this.querySelector('[data-builder-content]');
        this.progressContainer = this.querySelector('[data-progress-container]');
        this.backButton = this.querySelector('#back-btn');
        this.submitButton = this.querySelector('#inline-next-btn');
        this.minicart = document.querySelector('mini-cart');

        if (!this.contentContainer) {
            console.error('CRITICAL ERROR: The builder content container with attribute [data-builder-content] was not found in the DOM.');
        }
    }

    /**
     * The main initialization sequence for the builder.
     */
    async initialize() {
        this.uiFeedback.showLoading();
        this.stateManager.isInitializing = true;
        this.stateManager.loadStateFromURL();

        try {
            // Determine if the page was reloaded. This flag is used at the end of the function.
            const navEntries = performance.getEntriesByType("navigation");
            const isPageRefresh = navEntries.length > 0 && navEntries[0].type === 'reload';

            let apiPayload = {};

            if (this.stateManager.sharedConfigIdFromUrl) {
                // Load the snapshot data but do NOT modify it here.
                const snapshotData = await this.apiService.loadSnapshot(this.stateManager.sharedConfigIdFromUrl);
                this.stateManager.updateStateFromApiResponse(snapshotData);
                this.stateManager.wasStep2Skipped = this.stateManager.currentStep === 3 && !snapshotData.context?.['environment'];
            } else if (this.stateManager.contextFromUrl) {
                // Handle context from Mount Builder
                apiPayload = this.stateManager.contextFromUrl;
                if (this.stateManager.stepFromUrl) this.stateManager.currentStep = this.stateManager.stepFromUrl;

                try {
                    const responseData = await this.apiService.fetchData(apiPayload);

                    // Check if the API returned an error message (invalid data)
                    if (responseData.error_message) {
                        console.warn('API returned error:', responseData.error_message);
                        await this.resetToInitialState(responseData.error_message);
                        return;
                    }

                    this.stateManager.updateStateFromApiResponse(responseData);
                } catch (error) {
                    console.error('Error fetching data with context:', error);
                    // Reset to initial state on any error
                    await this.resetToInitialState();
                    return;
                }
            } else {
                this.stateManager.initState();
                const responseData = await this.apiService.fetchData({});
                this.stateManager.updateStateFromApiResponse(responseData);
            }

            // This will render Step 3. The logic in GDSCardManager will now handle the default selections.
            await this.stepOrchestrator.renderCurrentStep();

            // --- CORRECTED SELECTION LOGIC ---
            // Only apply custom selections IF IT IS NOT A PAGE REFRESH.
            if (!isPageRefresh) {
                let selectionsToApply = this.stateManager.selectionsFromUrl || this.stateManager.context?.selections;

                if (selectionsToApply && this.stateManager.currentStep === 3) {
                    // console.log('[GDSBuilder] Applying custom selections from URL/snapshot.');
                    await this.urlManager.applySelectionsFromURL(selectionsToApply);
                }
            } else {
                // console.log('[GDSBuilder] Page refresh detected. Custom selections will not be applied.');
            }

        } catch (error) {
            console.error('Failed to initialize GDSBuilder:', error);
            this.uiFeedback.showError('There was a problem loading the builder. Please try refreshing.');
        } finally {
            this.stateManager.isInitializing = false;
            this.classList.remove('hidden');
            this.uiFeedback.hideLoading();

            const parentSection = this.closest('#gds-builder-section');
            if (parentSection) {
                parentSection.classList.remove('hidden');
            } else {
                console.warn('[GDSBuilder] Parent section not found');
            }
        }
    }

    async resetToInitialState(errorMessage = null) {
        try {
            // Clear the URL parameters
            const url = new URL(window.location);
            url.search = '';
            window.history.replaceState({}, '', url);

            // Reset the state manager
            this.stateManager.initState();
            this.stateManager.contextFromUrl = null;
            this.stateManager.sharedConfigIdFromUrl = null;
            this.stateManager.selectionsFromUrl = null;
            this.stateManager.stepFromUrl = null;

            // Clear any existing content
            if (this.contentContainer) {
                this.contentContainer.innerHTML = '';
            }

            // Show loading while fetching initial data
            this.uiFeedback.showLoading();

            // Fetch initial data with empty context
            const responseData = await this.apiService.fetchData({});
            this.stateManager.updateStateFromApiResponse(responseData);

            // Hide loading
            this.uiFeedback.hideLoading();

            // Render the first step
            await this.stepOrchestrator.renderCurrentStep();

            // Show a brief message
            const message = document.createElement('div');
            message.className = 'fixed top-20 left-1/2 transform -translate-x-1/2 bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded z-50';
            message.textContent = errorMessage || 'Invalid device configuration. Starting fresh.';
            document.body.appendChild(message);

            // Remove the message after 3 seconds
            setTimeout(() => {
                if (message.parentNode) {
                    message.remove();
                }
            }, 3000);

        } catch (resetError) {
            console.error('Failed to reset to initial state:', resetError);
            this.uiFeedback.showError('Unable to load the builder. Please refresh the page.');
        }
    }

    /**
     * Sets up the sticky behavior for the mobile action bar in Step 3.
     */
    setupStickyMobileBar() {
        // Only run on mobile
        if (window.innerWidth >= 768) return;

        const mobileBar = document.getElementById('mobile-action-bar');
        const placeholder = document.getElementById('mobile-action-bar-placeholder');

        if (!mobileBar || !placeholder) return;

        // Get the header height to position the bar below it when sticky
        const header = document.getElementById('shopify-section-header');
        const headerHeight = header ? header.offsetHeight : 66; // Default header height if not found

        let originalTop = mobileBar.getBoundingClientRect().top + window.scrollY;
        const barHeight = mobileBar.offsetHeight;

        placeholder.style.height = `${barHeight}px`;

        const handleScroll = () => {
            if (window.innerWidth >= 768) {
                 mobileBar.classList.remove('fixed');
                 placeholder.style.display = 'none';
                return;
            };

            const scrollPosition = window.scrollY;

            if (scrollPosition > originalTop) {
                if (!mobileBar.classList.contains('fixed')) {
                    mobileBar.classList.add('fixed');
                    mobileBar.style.top = `${headerHeight}px`;
                    mobileBar.style.left = '0';
                    mobileBar.style.right = '0';
                    mobileBar.style.zIndex = '39';
                    placeholder.style.display = 'block';
                }
            } else {
                mobileBar.classList.remove('fixed');
                mobileBar.style.top = '';
                placeholder.style.display = 'none';
            }
        };

        // Use a single, named event listener to avoid duplicates
        if (!this._boundHandleScroll) {
            this._boundHandleScroll = handleScroll;
            window.addEventListener('scroll', this._boundHandleScroll);
        }
    }

    verifyGDSInitialization() {
        const requiredManagers = [
            'stateManager',
            'apiService',
            'uiFeedback',
            'progressManager',
            'modalManager',
            'cardManager',
            'cartAndShareManager',
            'stepOrchestrator',
            'eventHandler',
            'urlManager'
        ];

        const missing = [];

        requiredManagers.forEach(manager => {
            if (!this[manager]) {
                missing.push(manager);
            }
        });

        if (missing.length > 0) {
            console.error('GDS Builder initialization failed. Missing managers:', missing);
            return false;
        }

        // console.log('✓ All GDS managers initialized successfully');
        return true;
    }

    /**
     * Resets the tutorial for testing purposes
     */
    resetTutorial() {
        if (this.cardTutorial) {
            this.cardTutorial.resetTutorial();
        }
    }
}

customElements.define('gds-builder', GDSBuilder);
